import React from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";

const HeroSection = styled.section`
  height: 600px;
  background: linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url("/images/slide_7.webp");
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  color: white;

  .hero-content {
    max-width: 600px;

    h1 {
      font-size: 3rem;
      margin-bottom: 1rem;
      color: white;
    }

    p {
      font-size: 1.2rem;
      margin-bottom: 2rem;
      line-height: 1.6;
    }

    .hero-buttons {
      display: flex;
      gap: 1rem;
      flex-wrap: wrap;
    }
  }

  @media (max-width: 768px) {
    height: 500px;

    .hero-content {
      h1 {
        font-size: 2rem;
      }

      p {
        font-size: 1rem;
      }

      .hero-buttons {
        flex-direction: column;
        align-items: flex-start;
      }
    }
  }
`;

const WelcomeSection = styled.section`
  padding: 80px 0;
  background: white;

  .container {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 4rem;
    align-items: center;
  }

  .welcome-content {
    h1 {
      span {
        color: #666;
        font-weight: 400;
      }
    }

    p {
      color: #666;
      margin-bottom: 1rem;
    }

    .read-more {
      color: #dc2626;
      font-weight: 500;
      text-decoration: underline;
    }
  }

  .welcome-image {
    img {
      width: 100%;
      height: auto;
      border-radius: 10px;
    }
  }

  @media (max-width: 768px) {
    .container {
      grid-template-columns: 1fr;
      gap: 2rem;
    }
  }
`;

const WhyChooseSection = styled.section`
  padding: 80px 0;
  background: #f8f9fa;

  .section-title {
    text-align: center;
    margin-bottom: 3rem;

    h2 {
      font-size: 2.5rem;
      color: #dc2626;
    }
  }

  .features-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 2rem;
  }

  .feature-card {
    background: white;
    padding: 2rem;
    text-align: center;
    border-radius: 10px;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
    transition: transform 0.3s ease;

    &:hover {
      transform: translateY(-10px);
    }

    .feature-icon {
      width: 90px;
      height: 90px;
      margin: 0 auto 1.5rem;

      img {
        width: 100%;
        height: 100%;
        object-fit: contain;
      }
    }

    h3 {
      color: #dc2626;
      font-size: 1.2rem;
    }
  }
`;

const ServicesSection = styled.section`
  padding: 80px 0;
  background: white;

  .section-title {
    text-align: center;
    margin-bottom: 3rem;

    h2 {
      font-size: 2.5rem;
      color: #dc2626;
    }

    p {
      color: #666;
      max-width: 800px;
      margin: 0 auto;
    }
  }

  .services-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 2rem;
  }

  .service-card {
    background: white;
    border-radius: 10px;
    overflow: hidden;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
    transition: transform 0.3s ease;
    position: relative;
    height: 300px;

    &:hover {
      transform: translateY(-10px);

      .service-overlay {
        opacity: 1;
      }
    }

    .service-bg {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .service-overlay {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(rgba(220, 38, 38, 0.9), rgba(185, 28, 28, 0.9));
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      text-align: center;
      padding: 2rem;
      opacity: 0;
      transition: opacity 0.3s ease;
      color: white;

      h3 {
        color: white;
        margin-bottom: 1rem;
      }

      p {
        margin-bottom: 1.5rem;
      }

      .btn {
        background: white;
        color: #dc2626;

        &:hover {
          background: #f8f9fa;
        }
      }
    }

    .service-title {
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      background: linear-gradient(transparent, rgba(0, 0, 0, 0.8));
      padding: 2rem 1rem 1rem;
      color: white;

      h3 {
        color: white;
      }
    }
  }
`;

const Home = () => {
  const features = [
    {
      icon: "/images/safe_icon.png",
      title: "Reliable",
    },
    {
      icon: "/images/price_icon.png",
      title: "Best Prices",
    },
    {
      icon: "/images/bserv_icon.png",
      title: "Best Services",
    },
    {
      icon: "/images/avail_icon.png",
      title: "24/7 Available",
    },
  ];

  const services = [
    {
      title: "Mobile Tyre Fitting",
      description: "Mobile fitting for truck and trailer tyres at your depot or roadside, on a schedule that suits your fleet.",
      image: "/images/ser_mtf.webp",
      link: "/services#mobile-fitting",
    },
    {
      // Breakdown cover removed per request
    },
    {
      title: "Reliable New Tyres",
      description: "High-quality tyres from trusted brands to ensure your safety and performance on the road.",
      image: "/images/ser_rnt.webp",
      link: "/services#new-tyres",
    },
    {
      title: "Tyre Puncture Repair",
      description: "Professional puncture repair services to extend the life of your tyres and save you money.",
      image: "/images/ser_tpr.webp",
      link: "/services#puncture-repair",
    },
    {
      title: "Tyre Rotation",
      description: "Regular tyre rotation services to ensure even wear and maximize tyre lifespan.",
      image: "/images/ser_tirot.webp",
      link: "/services#tyre-rotation",
    },
    {
      title: "Trailer tyre repair",
      description: "Comprehensive trailer tyre repair and maintenance services for all types of trailers.",
      image: "/images/tyre.webp",
      link: "/trailer-repair",
    },
  ];

  return (
    <>
      <HeroSection>
        <div className="container">
          <motion.div
            className="hero-content"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}>
            <h1>The Ultimate Road Train</h1>
            <p>
              Road Trains consist of a prime mover pulling two or more trailer rigs connected by a converter dolly. We provide comprehensive tyre and
              truck solutions for all your transport needs.
            </p>
            <div className="hero-buttons">
              <Link
                to="/contact"
                className="btn">
                Contact Us
              </Link>
              <Link
                to="/get-quote"
                className="btn btn-outline">
                Get a Quote
              </Link>
            </div>
          </motion.div>
        </div>
      </HeroSection>

      <WelcomeSection>
        <div className="container">
          <motion.div
            className="welcome-content"
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}>
            <h1>
              <span>Welcome to</span>
              <br />
              Ausgrip Tyres
            </h1>
            <p>
              Our competitive advantage is not being tied to any one particular brand. Supporting major tyre brands ensuring constant supply &
              customer choice, with several alternatives achieving the best possible value for any application, Ausgrip Tyres is well placed to assist
              customers in selecting the right tyre to satisfy all tyre needs.
            </p>
            <p>
              Offering a focused range of truck and trailer tyres for heavy vehicles and commercial fleets, with options for linehaul, metro, and
              off-highway work.
            </p>
            <p>
              Quality truck tyres at the best prices in Australia. We support rigid trucks, prime movers, trailers, and road trains. Our team of
              professional fitters will have your new tyres fitted and balanced promptly.
            </p>
            <Link
              to="/about"
              className="read-more">
              read more...
            </Link>
          </motion.div>

          <motion.div
            className="welcome-image"
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}>
            <img
              src="/images/welc_pic.webp"
              alt="Welcome"
            />
          </motion.div>
        </div>
      </WelcomeSection>

      <WhyChooseSection>
        <div className="container">
          <div className="section-title">
            <h2>Why Choose Us</h2>
          </div>

          <div className="features-grid">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                className="feature-card"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}>
                <div className="feature-icon">
                  <img
                    src={feature.icon}
                    alt={feature.title}
                  />
                </div>
                <h3>{feature.title}</h3>
              </motion.div>
            ))}
          </div>
        </div>
      </WhyChooseSection>

      <ServicesSection>
        <div className="container">
          <div className="section-title">
            <h2>Our Services</h2>
            <p>
              In today's fast-paced world, not only is tyre technology rapidly evolving, but there is an ever-increasing number of high-quality
              alternatives to traditional brands available.
            </p>
          </div>

          <div className="services-grid">
            {services.map((service, index) => (
              <motion.div
                key={index}
                className="service-card"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}>
                <img
                  src={service.image}
                  alt={service.title}
                  className="service-bg"
                />
                <div className="service-overlay">
                  <h3>{service.title}</h3>
                  <p>{service.description}</p>
                  <Link
                    to={service.link}
                    className="btn">
                    Read More
                  </Link>
                </div>
                <div className="service-title">
                  <h3>{service.title}</h3>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </ServicesSection>
    </>
  );
};

export default Home;
